import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Text,
  Textarea,
  VStack,
  Badge,
  useColorModeValue,
  useToast,
  Input,
  FormControl,
  FormLabel,
  Flex,
  IconButton,
  Spinner,
  Collapse,
  useDisclosure,
  InputGroup,
  InputRightElement,
  Select,
} from "@chakra-ui/react";
import {
  FiMonitor,
  FiSquare,
  FiVolume2,
  FiVolumeX,
  FiChevronDown,
  FiChevronUp,
  FiMessageCircle,
  FiClock,
  FiSend,
  FiCamera,
} from "react-icons/fi";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import { hitBeApi, hitBeApiFormData } from "../../../api/api";
import VoiceCreditPaymentModal from "../VoiceCreditPaymentModal";
import { appStore } from "../../../store/Store";
import {
  useAiFeatureColors,
  AiCreditsBanner,
  AiCreditsPaywall,
  AiHistorySidebar,
  AiHistoryItem,
  AiHeroCard,
  AiStepStrip,
} from "../ai/aiFeatureUi";
import { useAiCreditsGate } from "../../../hooks/useAiCreditsGate";
import {
  createScreenCaptureSession,
  formatRecordingDuration,
  formatTimestamp,
} from "../../../utils/screenCaptureListen";
import { speakBrowserAssistantReply } from "../../../utils/browserSpeechSpeak";
import ScreenListenSummaryView from "./ScreenListenSummaryView";
import ScreenListenMediaPanel from "./ScreenListenMediaPanel";
import { revokeBlobUrl } from "../../../utils/screenListenMedia";

function formatSessionDate(iso) {
  if (!iso) return "";
  try {
    return format(new Date(iso), "MMM d, yyyy · h:mm a");
  } catch {
    return "";
  }
}

export default function ScreenListen() {
  const toast = useToast();
  const dispatch = useDispatch();
  const {
    remaining: aiCreditsRemaining,
    canUse: canUseScreenAi,
    payOpen: voicePayOpen,
    setPayOpen: setVoicePayOpen,
    promptBuy: promptBuyCredits,
  } = useAiCreditsGate();
  const captureRef = useRef(null);
  const frameTextsRef = useRef([]);
  const frameSnapshotsRef = useRef([]);
  const pendingFrameAnalysisRef = useRef(new Set());
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const { isOpen: transcriptOpen, onToggle: toggleTranscript } = useDisclosure({
    defaultIsOpen: true,
  });

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);

  const [sessionTitle, setSessionTitle] = useState("");
  const [screenshotIntervalSec, setScreenshotIntervalSec] = useState(10);
  const [screenshotCount, setScreenshotCount] = useState(0);
  const [frameSnapshots, setFrameSnapshots] = useState([]);
  const [localAudioUrl, setLocalAudioUrl] = useState(null);
  const [analyzingFrame, setAnalyzingFrame] = useState(false);
  const [liveScreenText, setLiveScreenText] = useState("");
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryStructured, setSummaryStructured] = useState(null);
  const [speaking, setSpeaking] = useState(false);

  const [askQuestion, setAskQuestion] = useState("");
  const [askAnswer, setAskAnswer] = useState("");
  const [askLoading, setAskLoading] = useState(false);
  const [askScope, setAskScope] = useState("all");

  const {
    pageBg,
    cardBg,
    borderColor,
    muted,
    panelBg,
    accent,
  } = useAiFeatureColors("teal");
  const transcriptColor = useColorModeValue("gray.700", "gray.200");
  const askBg = useColorModeValue("purple.50", "purple.900");
  const liveBg = useColorModeValue("cyan.50", "cyan.900");

  const applyCredits = useCallback(
    (userTokenDetails) => {
      if (userTokenDetails) {
        const currentUser = appStore.getState().user || {};
        dispatch({
          type: "SET_USER",
          value: { ...currentUser, userTokenDetails },
        });
      }
    },
    [dispatch]
  );

  const revokeAllFramePreviews = useCallback(() => {
    frameSnapshotsRef.current.forEach((f) => revokeBlobUrl(f.previewUrl));
    frameSnapshotsRef.current = [];
    setFrameSnapshots([]);
  }, []);

  const revokeLocalAudio = useCallback(() => {
    setLocalAudioUrl((prev) => {
      revokeBlobUrl(prev);
      return null;
    });
  }, []);

  const buildLiveScreenPreview = useCallback((frames) => {
    if (!frames?.length) return "";
    return frames
      .map((f) => `[${formatTimestamp(f.elapsedSec)}]\n${f.text}`)
      .join("\n\n");
  }, []);

  const analyzeScreenshot = useCallback(
    async ({ blob, elapsedSec, index }) => {
      if (!canUseScreenAi) return;
      const frameIndex = Math.max(0, Math.floor(Number(index) || 0));
      const run = async () => {
        setAnalyzingFrame(true);
        try {
          const form = new FormData();
          form.append("screenshot", blob, `frame-${frameIndex}.jpg`);
          form.append("elapsedSec", String(elapsedSec));
          form.append("frameIndex", String(frameIndex));
          const res = await hitBeApiFormData(
            "dashboard/screen-listen/analyze-frame",
            form
          );
          const text = String(res?.data?.text || "").trim();
          if (text) {
            frameTextsRef.current = [
              ...frameTextsRef.current.filter(
                (f) => f.frameIndex !== frameIndex
              ),
              { elapsedSec, frameIndex, text },
            ].sort(
              (a, b) => (a.frameIndex ?? a.elapsedSec) - (b.frameIndex ?? b.elapsedSec)
            );
            frameSnapshotsRef.current = frameSnapshotsRef.current.map((f) =>
              f.frameIndex === frameIndex ? { ...f, text } : f
            );
            setFrameSnapshots([...frameSnapshotsRef.current]);
            setScreenshotCount(frameTextsRef.current.length);
            setLiveScreenText(buildLiveScreenPreview(frameTextsRef.current));
          }
          applyCredits(res?.data?.userTokenDetails);
        } catch (e) {
          const msg = String(e?.message || "").toLowerCase();
          if (msg.includes("credit") || msg.includes("token")) {
            promptBuyCredits();
          }
        } finally {
          setAnalyzingFrame(false);
        }
      };
      const task = run();
      pendingFrameAnalysisRef.current.add(task);
      try {
        await task;
      } finally {
        pendingFrameAnalysisRef.current.delete(task);
      }
    },
    [applyCredits, buildLiveScreenPreview, canUseScreenAi, promptBuyCredits]
  );

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await hitBeApi("dashboard/screen-listen/sessions", "GET");
      setSessions(res?.data?.sessions || []);
    } catch {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const loadSession = useCallback(async (id) => {
    if (!id) {
      setActiveSession(null);
      return;
    }
    try {
      const res = await hitBeApi(`dashboard/screen-listen/sessions/${id}`, "GET");
      const session = res?.data?.session;
      setActiveSession(session);
      if (session) {
        setTranscript(session.transcript || "");
        setSummary(session.summary || "");
        setSummaryStructured(session.summaryStructured || null);
        setSessionTitle(session.title || "");
      }
    } catch (e) {
      toast({
        title: "Could not load session",
        description: e.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [toast]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (activeSessionId) {
      loadSession(activeSessionId);
    }
  }, [activeSessionId, loadSession]);

  const selectSession = (id) => {
    revokeAllFramePreviews();
    revokeLocalAudio();
    setActiveSessionId(id);
    setAskAnswer("");
    setAskScope(id ? "one" : "all");
  };

  const startNewCapture = () => {
    setActiveSessionId(null);
    setActiveSession(null);
    setTranscript("");
    setSummary("");
    setSummaryStructured(null);
    setSessionTitle("");
    setAskAnswer("");
    setAskScope("all");
    frameTextsRef.current = [];
    revokeAllFramePreviews();
    revokeLocalAudio();
    setScreenshotCount(0);
    setLiveScreenText("");
  };

  const stopSpeaking = useCallback(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      captureRef.current?.stream?.getTracks?.().forEach((t) => t.stop());
      stopSpeaking();
      revokeAllFramePreviews();
      revokeLocalAudio();
    };
  }, [stopSpeaking, revokeAllFramePreviews, revokeLocalAudio]);

  const applyResult = (data) => {
    setTranscript(data.transcript || "");
    setSummary(data.summary || "");
    setSummaryStructured(data.summaryStructured || null);
    if (data.session?._id) {
      setActiveSessionId(data.session._id);
      setActiveSession(data.session);
      if (data.session.frames?.some((f) => f.imageUrl)) {
        revokeAllFramePreviews();
      }
    }
    applyCredits(data.userTokenDetails);
    loadSessions();
  };

  const onCaptureScreenshot = useCallback(
    async ({ blob, elapsedSec, index }) => {
      const frameIndex = Math.max(0, Math.floor(Number(index) || 0));
      const previewUrl = URL.createObjectURL(blob);
      frameSnapshotsRef.current = [
        ...frameSnapshotsRef.current.filter((f) => f.frameIndex !== frameIndex),
        { elapsedSec, frameIndex, blob, previewUrl, text: "" },
      ].sort(
        (a, b) => (a.frameIndex ?? a.elapsedSec) - (b.frameIndex ?? b.elapsedSec)
      );
      setFrameSnapshots([...frameSnapshotsRef.current]);
      setScreenshotCount(frameSnapshotsRef.current.length);
      await analyzeScreenshot({ blob, elapsedSec, index: frameIndex });
    },
    [analyzeScreenshot]
  );

  const startRecording = async () => {
    if (!canUseScreenAi) {
      promptBuyCredits();
      return;
    }
    try {
      stopSpeaking();
      frameTextsRef.current = [];
      revokeAllFramePreviews();
      revokeLocalAudio();
      setScreenshotCount(0);
      setLiveScreenText("");
      setTranscript("");
      setSummary("");
      setSummaryStructured(null);

      const capture = await createScreenCaptureSession({
        screenshotIntervalSec,
        onScreenshot: onCaptureScreenshot,
      });
      captureRef.current = capture;
      capture.start();
      startedAtRef.current = Date.now();
      setElapsedMs(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startedAtRef.current);
      }, 500);

      capture.stream.getVideoTracks()[0]?.addEventListener("ended", () => {
        if (captureRef.current) finishRecording().catch(() => {});
      });

      toast({
        title: "Capturing audio + screen",
        description: `Share the tab or window with your map, slide, or image — not this Screen AI page. Screenshots every ${screenshotIntervalSec}s.`,
        status: "info",
        duration: 7000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "Could not share screen",
        description: e.message,
        status: "warning",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const finishRecording = async () => {
    const capture = captureRef.current;
    if (!capture) return;
    captureRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecording(false);
    const durationSec = Math.round(
      (Date.now() - startedAtRef.current) / 1000
    );

    let blob;
    try {
      blob = await capture.stop();
    } catch (e) {
      toast({ title: "Recording error", description: e.message, status: "error" });
      return;
    }

    if (blob && blob.size >= 800) {
      revokeLocalAudio();
      setLocalAudioUrl(URL.createObjectURL(blob));
    }

    if (
      (!blob || blob.size < 800) &&
      frameTextsRef.current.length === 0 &&
      frameSnapshotsRef.current.length === 0
    ) {
      toast({
        title: "Nothing captured",
        description:
          "Record longer audio or ensure screenshots capture visible text.",
        status: "info",
      });
      return;
    }

    if (!canUseScreenAi) {
      promptBuyCredits();
      return;
    }

    if (pendingFrameAnalysisRef.current.size > 0) {
      await Promise.allSettled([...pendingFrameAnalysisRef.current]);
    }

    setProcessing(true);
    try {
      const form = new FormData();
      if (blob && blob.size >= 800) {
        form.append("audio", blob, "screen-capture.webm");
      }
      if (sessionTitle.trim()) form.append("title", sessionTitle.trim());
      form.append("durationSec", String(durationSec));
      form.append("captureIntervalSec", String(screenshotIntervalSec));
      form.append("frameTexts", JSON.stringify(frameTextsRef.current));
      const textBySec = new Map(
        frameTextsRef.current.map((f) => [f.elapsedSec, f.text])
      );
      const snapshots = frameSnapshotsRef.current;
      const frameMeta = snapshots.map((s, i) => ({
        elapsedSec: s.elapsedSec,
        frameIndex: s.frameIndex ?? i,
        text: String(
          s.text ||
            frameTextsRef.current.find(
              (f) => f.frameIndex === (s.frameIndex ?? i)
            )?.text ||
            textBySec.get(s.elapsedSec) ||
            ""
        ).trim(),
      }));
      snapshots.forEach((s, i) => {
        form.append("screenshots", s.blob, `frame-${i}.jpg`);
      });
      if (frameMeta.length) {
        form.append("frameMeta", JSON.stringify(frameMeta));
      }
      const res = await hitBeApiFormData("dashboard/screen-listen", form);
      applyResult(res?.data || {});
      toast({ title: "Saved & summarized", status: "success", duration: 3000 });
    } catch (e) {
      const msg = String(e?.message || "").toLowerCase();
      if (msg.includes("credit") || msg.includes("token")) {
        promptBuyCredits();
      }
      toast({
        title: "Processing failed",
        description: e.message,
        status: "error",
        duration: 7000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const summarizeText = async () => {
    if (!canUseScreenAi) {
      promptBuyCredits();
      return;
    }
    const text = String(transcript || "").trim();
    if (!text) {
      toast({ title: "Add a transcript first", status: "info" });
      return;
    }
    setProcessing(true);
    stopSpeaking();
    try {
      const res = await hitBeApi(
        "dashboard/screen-listen/summarize-text",
        "POST",
        { transcript: text, title: sessionTitle.trim() || undefined }
      );
      applyResult(res?.data || {});
      toast({ title: "Saved & summarized", status: "success" });
    } catch (e) {
      toast({ title: "Failed", description: e.message, status: "error" });
    } finally {
      setProcessing(false);
    }
  };

  const deleteSession = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this session from history?")) return;
    try {
      await hitBeApi(`dashboard/screen-listen/sessions/${id}`, "DELETE");
      if (activeSessionId === id) startNewCapture();
      loadSessions();
      toast({ title: "Deleted", status: "info", duration: 2000 });
    } catch (err) {
      toast({ title: "Delete failed", description: err.message, status: "error" });
    }
  };

  const askAi = async () => {
    if (!canUseScreenAi) {
      promptBuyCredits();
      return;
    }
    const q = askQuestion.trim();
    if (!q) return;
    if (askScope === "one" && !activeSessionId) {
      toast({
        title: "Select a session first",
        description:
          "Click a session in the history sidebar, then ask about its screenshots and summary.",
        status: "info",
        duration: 5000,
      });
      return;
    }
    setAskLoading(true);
    setAskAnswer("");
    try {
      const res = await hitBeApi("dashboard/screen-listen/ask", "POST", {
        question: q,
        sessionId:
          askScope === "one" && activeSessionId ? activeSessionId : undefined,
      });
      setAskAnswer(res?.data?.answer || "");
      applyCredits(res?.data?.userTokenDetails);
      const shots = Number(res?.data?.screenshotsUsed || 0);
      if (shots > 0) {
        toast({
          title: "Answer used your screenshots",
          description: `AI reviewed ${shots} saved screenshot${shots === 1 ? "" : "s"} from your session${askScope === "one" ? "" : "s"}.`,
          status: "success",
          duration: 4000,
        });
      }
    } catch (e) {
      toast({ title: "Ask failed", description: e.message, status: "error" });
    } finally {
      setAskLoading(false);
    }
  };

  const speakSummary = () => {
    const text =
      summaryStructured?.summary || summary || activeSession?.summary;
    if (!text) return;
    stopSpeaking();
    speakBrowserAssistantReply(text, {
      lang: "en-IN",
      onBeforeSpeak: () => setSpeaking(true),
      onAfterSpeak: () => setSpeaking(false),
    });
  };

  const hasContent = !!(summary || summaryStructured || activeSession);

  const mediaSession = activeSession;
  const showMediaPanel =
    recording ||
    frameSnapshots.length > 0 ||
    localAudioUrl ||
    mediaSession?.hasAudio ||
    (mediaSession?.frames?.length > 0);

  const mediaDurationLabel = recording
    ? formatRecordingDuration(elapsedMs)
    : mediaSession?.durationLabel;

  const viewingHistory =
    !!activeSessionId && !recording && !processing;

  return (
    <Box bg={pageBg} minH="100%" py={{ base: 3, md: 5 }} px={{ base: 2, md: 4 }}>
      <Box maxW="1280px" mx="auto">
        <Flex
          direction={{ base: "column", lg: "row" }}
          gap={5}
          align="stretch"
        >
          <AiHistorySidebar
            theme="teal"
            newLabel="New capture"
            isNewActive={!activeSessionId}
            canUseNew={canUseScreenAi}
            onBuyCredits={promptBuyCredits}
            onNew={startNewCapture}
            onRefresh={loadSessions}
            loading={sessionsLoading}
            emptyText="No sessions yet. Start your first screen capture."
          >
            {sessions.map((s) => (
              <AiHistoryItem
                key={s._id}
                theme="teal"
                active={activeSessionId === s._id}
                title={s.title}
                meta={
                  <HStack spacing={1} mt={1} color={muted} flexWrap="wrap">
                    <Icon as={FiClock} boxSize={3} />
                    <Text fontSize="xs">{formatSessionDate(s.createdAt)}</Text>
                    {s.durationLabel ? (
                      <Badge size="sm" colorScheme={accent} variant="subtle">
                        {s.durationLabel}
                      </Badge>
                    ) : null}
                    {s.frameCaptureCount > 0 ? (
                      <Badge size="sm" variant="outline">
                        {s.frameCaptureCount} shots
                      </Badge>
                    ) : null}
                  </HStack>
                }
                preview={s.summaryPreview}
                onSelect={() => selectSession(s._id)}
                onDelete={(e) => deleteSession(s._id, e)}
              />
            ))}
          </AiHistorySidebar>

          {/* Main panel */}
          <Box flex="1" minW={0}>
            <VStack align="stretch" spacing={5}>
              {viewingHistory ? (
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  borderWidth="1px"
                  borderColor={borderColor}
                  p={{ base: 4, md: 5 }}
                  boxShadow="sm"
                >
                  <HStack justify="space-between" align="start" flexWrap="wrap" gap={3}>
                    <Box minW={0}>
                      <Heading size="md" noOfLines={2}>
                        {activeSession?.title || sessionTitle || "Session"}
                      </Heading>
                      {activeSession?.createdAt ? (
                        <Text fontSize="sm" color={muted} mt={1}>
                          {formatSessionDate(activeSession.createdAt)}
                          {activeSession.durationLabel
                            ? ` · ${activeSession.durationLabel}`
                            : ""}
                        </Text>
                      ) : null}
                    </Box>
                    <Button
                      size="sm"
                      colorScheme="teal"
                      leftIcon={<Icon as={FiMonitor} />}
                      onClick={() => {
                        if (!canUseScreenAi) {
                          promptBuyCredits();
                          return;
                        }
                        startNewCapture();
                      }}
                      flexShrink={0}
                    >
                      New capture
                    </Button>
                  </HStack>
                </Box>
              ) : null}

              {/* Hero capture — new session or while recording */}
              {!viewingHistory ? (
              <Box position="relative">
              <AiCreditsPaywall
                show={!canUseScreenAi && !recording}
                featureName="Screen AI"
                onBuy={promptBuyCredits}
                theme="teal"
              />
              <AiHeroCard
                theme="teal"
                title="Screen AI"
                subtitle="Share a tab or meeting. We snapshot the screen on a timer, explain what’s on screen clearly, and blend in your voice when audio is available."
                recording={recording}
              >
                  <AiCreditsBanner
                    canUse={canUseScreenAi}
                    remaining={aiCreditsRemaining}
                    onBuy={promptBuyCredits}
                    variant="hero"
                  />
                  <AiStepStrip
                    theme="teal"
                    steps={[
                      { label: "Share screen", hint: "Pick a tab with slides or a live class." },
                      { label: "Live snapshots", hint: "AI reads each screenshot while you teach." },
                      { label: "Summary & ask", hint: "Get notes, audio, and chat with your session." },
                    ]}
                  />
                  <Box color="white">
                  <HStack spacing={4} mb={4} flexWrap="wrap" maxW="lg">
                    <FormControl maxW="220px">
                      <FormLabel fontSize="xs" opacity={0.9} color="white">
                        Screenshot interval
                      </FormLabel>
                      <Select
                        size="sm"
                        color="white"
                        bg="whiteAlpha.200"
                        borderColor="whiteAlpha.400"
                        value={String(screenshotIntervalSec)}
                        onChange={(e) =>
                          setScreenshotIntervalSec(Number(e.target.value))
                        }
                        isDisabled={recording || processing || !canUseScreenAi}
                      >
                        <option value="2">Every 2 seconds</option>
                        <option value="5">Every 5 seconds</option>
                        <option value="10">Every 10 seconds</option>
                        <option value="15">Every 15 seconds</option>
                        <option value="30">Every 30 seconds</option>
                      </Select>
                    </FormControl>
                    <FormControl flex="1" minW="200px">
                      <FormLabel fontSize="xs" opacity={0.9} color="white">
                        Session title
                      </FormLabel>
                    <Input
                      size="sm"
                      color="white"
                      bg="whiteAlpha.200"
                      borderColor="whiteAlpha.400"
                      _placeholder={{ color: "whiteAlpha.700" }}
                      placeholder="e.g. Running form tips — YouTube"
                      value={sessionTitle}
                      onChange={(e) => setSessionTitle(e.target.value)}
                      isDisabled={recording || processing || !canUseScreenAi}
                    />
                    </FormControl>
                  </HStack>
                  <HStack spacing={3} flexWrap="wrap">
                    {!recording ? (
                      <Button
                        size="lg"
                        colorScheme="whiteAlpha"
                        bg="white"
                        color="teal.700"
                        leftIcon={<Icon as={FiMonitor} boxSize={5} />}
                        onClick={startRecording}
                        isDisabled={processing || !canUseScreenAi}
                        _hover={{ bg: "gray.100" }}
                      >
                        Share screen & analyze
                      </Button>
                    ) : (
                      <Button
                        size="lg"
                        colorScheme="red"
                        leftIcon={<Icon as={FiSquare} />}
                        onClick={finishRecording}
                      >
                        Stop · {formatRecordingDuration(elapsedMs)}
                      </Button>
                    )}
                    {!canUseScreenAi && !recording ? (
                      <Button
                        size="md"
                        colorScheme="yellow"
                        variant="solid"
                        onClick={promptBuyCredits}
                      >
                        Buy AI Usage Credits
                      </Button>
                    ) : null}
                    {recording ? (
                      <Badge
                        bg="blackAlpha.500"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="md"
                      >
                        <Icon as={FiCamera} mr={1} />
                        {screenshotCount} screenshots
                        {analyzingFrame ? " · analyzing…" : ""}
                      </Badge>
                    ) : null}
                    {processing ? (
                      <HStack>
                        <Spinner size="sm" color="white" />
                        <Text fontSize="sm">Merging & summarizing…</Text>
                      </HStack>
                    ) : null}
                  </HStack>
                  <Text fontSize="xs" mt={4} opacity={0.9} color="white">
                    Share the window or tab with your content (PDF, map, slide, app) —
                    not this page. Voice is optional; screenshots alone can
                    capture and explain what’s on screen.
                  </Text>
                  </Box>
              </AiHeroCard>
              </Box>
              ) : null}

              {showMediaPanel ? (
                <ScreenListenMediaPanel
                  sessionId={mediaSession?._id || activeSessionId}
                  durationLabel={mediaDurationLabel}
                  durationSec={
                    recording
                      ? Math.floor(elapsedMs / 1000)
                      : mediaSession?.durationSec
                  }
                  hasAudio={recording ? !!localAudioUrl : mediaSession?.hasAudio}
                  audioUrl={mediaSession?.audioUrl}
                  audioMimeType={mediaSession?.audioMimeType}
                  frames={mediaSession?.frames}
                  liveFrames={
                    recording ||
                    (processing && !(mediaSession?.frames?.length > 0))
                      ? frameSnapshots
                      : []
                  }
                  localAudioUrl={localAudioUrl}
                />
              ) : null}

              {recording && liveScreenText ? (
                <Box
                  bg={liveBg}
                  borderRadius="2xl"
                  borderWidth="1px"
                  borderColor={borderColor}
                  p={4}
                  boxShadow="sm"
                >
                  <HStack mb={2}>
                    <Icon as={FiCamera} color="cyan.600" />
                    <Heading size="sm">Live on-screen text</Heading>
                  </HStack>
                  <Text
                    fontSize="sm"
                    lineHeight="tall"
                    whiteSpace="pre-wrap"
                    maxH="200px"
                    overflowY="auto"
                    color={transcriptColor}
                  >
                    {liveScreenText}
                  </Text>
                </Box>
              ) : null}

              {/* Summary */}
              {hasContent ? (
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  borderWidth="1px"
                  borderColor={borderColor}
                  p={{ base: 4, md: 6 }}
                  boxShadow="sm"
                >
                  <HStack justify="space-between" mb={4} flexWrap="wrap">
                    <Heading size="md">AI summary</Heading>
                    <HStack>
                      {speaking ? (
                        <Button
                          size="sm"
                          variant="outline"
                          leftIcon={<FiVolumeX />}
                          onClick={stopSpeaking}
                        >
                          Stop
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          leftIcon={<FiVolume2 />}
                          onClick={speakSummary}
                        >
                          Read aloud
                        </Button>
                      )}
                    </HStack>
                  </HStack>
                  <ScreenListenSummaryView
                    summaryStructured={
                      summaryStructured || activeSession?.summaryStructured
                    }
                    summary={summary || activeSession?.summary}
                  />
                </Box>
              ) : null}

              {/* Transcript */}
              <Box
                bg={cardBg}
                borderRadius="2xl"
                borderWidth="1px"
                borderColor={borderColor}
                overflow="hidden"
                boxShadow="sm"
              >
                <HStack
                  px={5}
                  py={3}
                  justify="space-between"
                  cursor="pointer"
                  onClick={toggleTranscript}
                  _hover={{ bg: panelBg }}
                >
                  <Heading size="sm">Full transcript (audio + screen merged)</Heading>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label="Toggle"
                    icon={transcriptOpen ? <FiChevronUp /> : <FiChevronDown />}
                  />
                </HStack>
                <Collapse in={transcriptOpen}>
                  <Box px={5} pb={5}>
                    <Textarea
                      value={transcript}
                      onChange={(e) => setTranscript(e.target.value)}
                      placeholder="Transcript appears after recording…"
                      rows={10}
                      fontSize="sm"
                      lineHeight="tall"
                      color={transcriptColor}
                      bg={panelBg}
                      borderColor={borderColor}
                      mb={3}
                    />
                    <Button
                      size="sm"
                      colorScheme="teal"
                      onClick={summarizeText}
                      isLoading={processing}
                      isDisabled={recording || !canUseScreenAi}
                    >
                      Save & summarize transcript
                    </Button>
                  </Box>
                </Collapse>
              </Box>

              {/* Ask AI */}
              <Box
                bg={askBg}
                borderRadius="2xl"
                borderWidth="1px"
                borderColor={borderColor}
                p={{ base: 4, md: 6 }}
                boxShadow="sm"
              >
                <HStack mb={3} spacing={2}>
                  <Icon as={FiMessageCircle} color="purple.500" boxSize={5} />
                  <Heading size="md">Ask about your past sessions</Heading>
                </HStack>
                <Text fontSize="sm" color={muted} mb={4} lineHeight="tall">
                  Ask using saved summaries, transcripts, and screenshots from your
                  sessions. Pick a session in history for &quot;This session only&quot;,
                  or use all history. No need to share your screen again.
                </Text>
                <HStack mb={3} spacing={2} flexWrap="wrap">
                  <Badge
                    cursor="pointer"
                    colorScheme={askScope === "all" ? "purple" : "gray"}
                    variant={askScope === "all" ? "solid" : "outline"}
                    onClick={() => setAskScope("all")}
                  >
                    All history
                  </Badge>
                  {activeSessionId ? (
                    <Badge
                      cursor="pointer"
                      colorScheme={askScope === "one" ? "purple" : "gray"}
                      variant={askScope === "one" ? "solid" : "outline"}
                      onClick={() => setAskScope("one")}
                    >
                      This session only
                    </Badge>
                  ) : null}
                </HStack>
                <InputGroup size="md" mb={askAnswer ? 4 : 0}>
                  <Input
                    pr="4.5rem"
                    bg={cardBg}
                    placeholder='e.g. "What were the four running mistakes mentioned?"'
                    value={askQuestion}
                    onChange={(e) => setAskQuestion(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        askAi();
                      }
                    }}
                  />
                  <InputRightElement width="4.5rem">
                    <Button
                      h="1.75rem"
                      size="sm"
                      colorScheme="purple"
                      isLoading={askLoading}
                      onClick={askAi}
                      isDisabled={!canUseScreenAi}
                      leftIcon={<FiSend />}
                    >
                      Ask
                    </Button>
                  </InputRightElement>
                </InputGroup>
                {askAnswer ? (
                  <Box
                    mt={4}
                    p={4}
                    bg={cardBg}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={borderColor}
                  >
                    <HStack justify="space-between" mb={2} flexWrap="wrap">
                      <Text fontSize="xs" fontWeight="semibold" color={muted}>
                        Answer
                      </Text>
                      {askScope === "one" && activeSession?.title ? (
                        <Badge size="sm" colorScheme="purple" variant="subtle">
                          {activeSession.title}
                        </Badge>
                      ) : null}
                    </HStack>
                    <Text fontSize="sm" lineHeight="tall" whiteSpace="pre-wrap">
                      {askAnswer}
                    </Text>
                  </Box>
                ) : null}
              </Box>
            </VStack>
          </Box>
        </Flex>
      </Box>

      <VoiceCreditPaymentModal
        isOpen={voicePayOpen}
        onClose={() => setVoicePayOpen(false)}
        variant={canUseScreenAi ? "topUp" : "exhausted"}
      />
    </Box>
  );
}
